import {useCallback, useEffect, useState} from 'react';
import type {Module, TransformedModule} from '../types/modules';
import {getModules} from '../services/authService';
import {mapIconStringToComponent} from '../utils/iconMapper';

// Work Evidence is now a tab of the Payroll page, so it is hidden from the sidebar / mobile nav
// (the route itself still resolves, see WorkEvidencePage). The backend may still return the module.
const HIDDEN_NAV_SEGMENTS = new Set(['Work Evidence']);

function withoutHiddenModules(modules: Module[]): Module[] {
    return modules
        .filter((m) => !HIDDEN_NAV_SEGMENTS.has(m.segment?.trim()))
        .map((m) => (m.children ? {...m, children: withoutHiddenModules(m.children)} : m));
}

export function useModules(token: string | null, user: string | null, language?: string) {
    const [modules, setModules] = useState<Module[]>([]);
    const [navigation, setNavigation] = useState<TransformedModule[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const transformModules = useCallback((modules: Module[]): TransformedModule[] => {
        return modules.map(module => {
            const {children, icon, title, translation, ...rest} = module;
            const transformed: TransformedModule = {
                ...rest,
                title: translation || title,
                icon: mapIconStringToComponent(icon),
            };
            if (children) {
                transformed.children = transformModules(children);
            }
            return transformed;
        });
    }, []);

    useEffect(() => {
        const fetchModules = async (): Promise<void> => {
            if (!token || !user) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const result = await getModules(token, language);

                if (result.success && result.modules) {
                    setModules(result.modules);
                    setNavigation(transformModules(withoutHiddenModules(result.modules)));
                    setError(null);
                } else {
                    setError(result.error || 'Failed to fetch modules');
                }
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to fetch modules';
                setError(errorMessage);
                console.error('Error fetching modules:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchModules();
    }, [user, token, language, transformModules]);

    return {modules, navigation, loading, error};
}
